import { datadogRum } from '@datadog/browser-rum'
import type { RumEvent } from '@datadog/browser-rum'

interface DatadogRumConfig {
  applicationId: string
  clientToken: string
  site: string
  service: string
  env: string
  version: string
  sessionReplaySampleRate: number
  defaultPrivacyLevel: 'allow' | 'mask' | 'mask-user-input'
  enablePrivacyForActionName: boolean
  adminId: number | string | null
  impersonating: boolean
}

// test/lib/datadog_rum_route_scrubbing_test.rb keeps a second Ruby copy of these three patterns
// and walks every :key/:token route segment in config/routes.rb against them -- keep both copies
// identical, or a route this test approved can still leak through this file.
const KEY_REGEX = /\b[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}\b/g // constants.rb KEY_REGEX
const PUBLIC_KEY_REGEX = /\/[A-Z0-9]{12}(?=\/|$)/g // constants.rb PUBLIC_KEY_REGEX
const TOKEN_SEGMENT_REGEX = /\/(invitation_link|invitations|proxy_intentions|organizations)\/[^/?#]+/g

const scrubPath = (path: string): string => (
  path
    .replace(TOKEN_SEGMENT_REGEX, '/$1/[redacted]')
    .replace(PUBLIC_KEY_REGEX, '/[redacted]')
    .replace(KEY_REGEX, '[redacted]')
)

// Admin URLs carry voter PII in query strings (elections_controller.rb `params[:q]`,
// billing_accounts_controller.rb `params[:email]`) -- the SDK's editable-property set lets
// beforeSend replace a url string wholesale but not selectively redact query params, so the
// whole query string and fragment are dropped rather than partially scrubbed.
const scrubUrl = (url: string): string => scrubPath(url.split(/[?#]/)[0])

const scrubText = (text: string): string => text.replace(KEY_REGEX, '[redacted]')

// beforeSend can edit the fields below but cannot discard view events, so this always returns
// true -- scrubbing, not filtering, is the control here.
/* eslint-disable no-param-reassign -- beforeSend's contract with the SDK is to mutate its event
   object in place; there is no other way to edit an outgoing RUM event. */
function beforeSend(event: RumEvent): boolean {
  if (event.view.url) event.view.url = scrubUrl(event.view.url)
  if (event.view.referrer) event.view.referrer = scrubUrl(event.view.referrer)

  if (event.type === 'view' && event.view.performance?.lcp?.resource_url) {
    event.view.performance.lcp.resource_url = scrubUrl(event.view.performance.lcp.resource_url)
  }

  if (event.type === 'resource' && event.resource.url) {
    event.resource.url = scrubUrl(event.resource.url)
  }

  if (event.type === 'error') {
    if (event.error.resource?.url) event.error.resource.url = scrubUrl(event.error.resource.url)
    if (event.error.message) event.error.message = scrubText(event.error.message)
    if (event.error.stack) event.error.stack = scrubText(event.error.stack)
  }

  if (event.type === 'long_task') {
    event.long_task.scripts?.forEach((script) => {
      if (script.source_url) script.source_url = scrubUrl(script.source_url)
    })
  }

  return true
}
/* eslint-enable no-param-reassign */

const readConfig = (): DatadogRumConfig | null => {
  const meta = document.querySelector('meta[name="datadog-rum-config"]') as HTMLMetaElement | null
  if (!meta?.content) return null

  try {
    return JSON.parse(meta.content) as DatadogRumConfig
  } catch {
    return null
  }
}

// Named to match the other utils/ modules' export style (turbo_events, flex_rows, jquery).
// eslint-disable-next-line import/prefer-default-export
export function initDatadogRum(): void {
  const config = readConfig()
  if (!config) return

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: config.env,
    version: config.version,
    sessionSampleRate: 100,
    // Server-driven, not hardcoded: DatadogRum#session_replay? turns unmasked replay ON outside
    // production and OFF in it, so a real voter's ballot is never recorded. Change it there.
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    defaultPrivacyLevel: config.defaultPrivacyLevel,
    trackUserInteractions: true,
    enablePrivacyForActionName: config.enablePrivacyForActionName,
    allowedTracingUrls: [{ match: window.location.origin, propagatorTypes: ['tracecontext', 'datadog'] }],
    // NOT the 100 default -- at 100 every sampled RUM session forces
    // `x-datadog-sampling-priority: 1` on its XHRs, inflating APM ingestion on exactly the
    // high-traffic election days this exists to observe.
    traceSampleRate: 20,
    beforeSend,
  })

  if (config.impersonating) {
    datadogRum.setGlobalContextProperty('impersonating', true)
  } else if (config.adminId) {
    datadogRum.setUser({ id: String(config.adminId) })
  }
}
